import React, { useState } from "react";

const MortgageCalculator = () => {
  const [homePrice, setHomePrice] = useState(1000000);
  const [downPayment, setDownPayment] = useState(350000);
  const [interestRate, setInterestRate] = useState(6.0);
  const [loanTerm, setLoanTerm] = useState(30);
  const [taxRate, setTaxRate] = useState(1.21);
  const [homeInsurance, setHomeInsurance] = useState(3996);
  const [hoaFees, setHoaFees] = useState(545);
  const [utilities, setUtilities] = useState({ water: 0, gas: 0, internet: 0, electric: 0 });

  const [homePriceError, setHomePriceError] = useState("");
  const [downPaymentError, setDownPaymentError] = useState("");
  const [interestRateError, setInterestRateError] = useState("");
  const [utilityErrors, setUtilityErrors] = useState({});

  const validateNumber = (value, min = 0) => {
    const cleaned = value.replace(/^0+(?!$)/, "");
    return cleaned && !isNaN(cleaned) && Number(cleaned) >= min ? "" : "Invalid value";
  };

  const handlePriceChange = (e) => {
    const val = e.target.value.replace(/,/g, '').replace(/^0+(?!$)/, "");
    const parsed = Number(val);
    setHomePriceError(validateNumber(val, 0) ? "Invalid home price" : "");
    setHomePrice(isNaN(parsed) ? 0 : parsed);
  };

  const handleDownPaymentChange = (e) => {
    const val = e.target.value.replace(/,/g, '').replace(/^0+(?!$)/, "");
    const parsed = Number(val);
    setDownPaymentError(validateNumber(val, 0) ? "Invalid down payment" : "");
    setDownPayment(isNaN(parsed) ? 0 : parsed);
  };

  const handleInterestRateChange = (e) => {
    const val = e.target.value.replace(/,/g, '').replace(/^0+(?!$)/, "");
    const parsed = Number(val);
    setInterestRateError(validateNumber(val, 0.1) ? "Invalid interest rate" : "");
    setInterestRate(isNaN(parsed) ? 0 : parsed);
  };

  const handleUtilityChange = (key, value) => {
    const cleaned = value.replace(/,/g, '').replace(/^0+(?!$)/, "");
    const error = validateNumber(cleaned);
    setUtilityErrors((prev) => ({ ...prev, [key]: error ? "Invalid" : "" }));
    setUtilities({ ...utilities, [key]: Number(cleaned) });
  };

  const loanAmount = homePrice - downPayment;
  const monthlyInterest = interestRate / 100 / 12;
  const numPayments = loanTerm * 12;

  const monthlyPrincipalInterest =
    loanAmount > 0
      ? (loanAmount * monthlyInterest) / (1 - Math.pow(1 + monthlyInterest, -numPayments))
      : 0;

  const [customMortgageInsurance, setCustomMortgageInsurance] = useState(100);
  const mortgageInsurance = downPayment / homePrice < 0.2 ? customMortgageInsurance : 0;
  const monthlyTax = (homePrice * (taxRate / 100)) / 12;
  const monthlyInsurance = homeInsurance / 12;
  const utilityTotal = Object.values(utilities).reduce((a, b) => a + Number(b), 0);
  const totalMonthly = monthlyPrincipalInterest + mortgageInsurance + monthlyTax + monthlyInsurance + Number(hoaFees) + utilityTotal;

  return (
    <div className="mt-10 p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-md border border-gray-200 max-w-4xl">
      <h2 className="text-2xl sm:text-3xl font-semibold mb-6 text-gray-800 text-left tracking-tight"> Monthly Expenditure Estimation</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Home Purchase Price</label>
          <div className="relative">
            <span className="absolute left-3 top-2.5 text-gray-500">$</span>
            <input
              type="text"
              value={homePrice.toLocaleString()}
              onChange={handlePriceChange}
              className={`w-full rounded border pl-6 pr-3 py-2 focus:outline-none focus:ring-2 ${homePriceError ? "border-red-500 bg-red-50" : "border-gray-300"}`}
            />
          </div>
          {homePriceError && <p className="text-xs text-red-600 mt-1">{homePriceError}</p>}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Down Payment</label>
          <div className="relative">
            <span className="absolute left-3 top-2.5 text-gray-500">$</span>
            <input
              type="text"
              value={downPayment.toLocaleString()}
              onChange={handleDownPaymentChange}
              className={`w-full rounded border pl-6 pr-10 py-2 focus:outline-none focus:ring-2 ${downPaymentError ? "border-red-500 bg-red-50" : "border-gray-300"}`}
            />
            <span className="absolute right-3 top-2.5 text-gray-500 text-sm">
              {homePrice > 0 ? ((downPayment / homePrice) * 100).toFixed(1) + '%' : ''}
            </span>
          </div>
          {homePrice > 0 && downPayment / homePrice < 0.2 && (
            <p className="text-xs text-yellow-600 mt-1">Down Payment Less Than 20% May Require Mortgage Insurance</p>
          )}
          {downPaymentError && <p className="text-xs text-red-600 mt-1">{downPaymentError}</p>}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Loan Term (years)</label>
          <select
            className="w-full rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={loanTerm}
            onChange={(e) => setLoanTerm(Number(e.target.value))}
          >
            <option value={30}>30</option>
            <option value={15}>15</option>
            <option value={10}>10</option>
          </select>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Interest Rate (%)</label>
          <input
            type="number"
            step="0.01"
            value={interestRate.toLocaleString()}
            onChange={handleInterestRateChange}
            className={`w-full rounded border px-3 py-2 focus:outline-none focus:ring-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${interestRateError ? "border-red-500 bg-red-50" : "border-gray-300"}`}
          />
          {interestRateError && <p className="text-xs text-red-600 mt-1">{interestRateError}</p>}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Tax Rate (%)</label>
          <input
            type="number"
            step="0.01"
            className="w-full h-[42px] rounded border px-3 py-2 focus:outline-none focus:ring-2 border-gray-300
          [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            value={taxRate.toLocaleString()}
            onChange={(e) => setTaxRate(Number(e.target.value))}
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Insurance ($/year)</label>
          <div className="relative">
            <span className="absolute left-3 top-2.5 text-gray-500">$</span>
            <input
              type="text"
              className="w-full rounded border border-gray-300 pl-6 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={homeInsurance.toLocaleString()}
              onChange={(e) => setHomeInsurance(Number(e.target.value.replace(/,/g, '')))}
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">HOA ($/month)</label>
          <div className="relative">
            <span className="absolute left-3 top-2.5 text-gray-500">$</span>
            <input
              type="text"
              className="w-full rounded border border-gray-300 pl-6 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={hoaFees.toLocaleString()}
              onChange={(e) => setHoaFees(Number(e.target.value.replace(/,/g, '')))}
            />
          </div>
        </div>

        {downPayment / homePrice < 0.2 && (
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Mortgage Insurance ($/month)</label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-gray-500">$</span>
              <input
                type="text"
                className="w-full rounded border border-gray-300 pl-6 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={customMortgageInsurance.toLocaleString()}
                onChange={(e) => setCustomMortgageInsurance(Number(e.target.value.replace(/,/g, '')))}
              />
            </div>
          </div>
        )}
      </div>

      <div className="mt-6">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Monthly Utilities</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Object.keys(utilities).map((key) => (
            <div key={key}>
              <label className="block text-xs text-gray-600 capitalize mb-1">{key}</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-500">$</span>
                <input
                  type="text"
                  className={`w-full rounded border pl-6 pr-3 py-2 focus:outline-none focus:ring-2 ${utilityErrors[key] ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                  value={utilities[key].toLocaleString()}
                  onChange={(e) => handleUtilityChange(key, e.target.value)}
                />
                {utilityErrors[key] && <p className="text-xs text-red-600 mt-1">Invalid {key}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 border-t pt-4">
        <h3 className="text-xl font-bold text-gray-800">
          Estimated Monthly Cost: <span className="text-primary">${totalMonthly.toFixed(0)}</span>
        </h3>
        <p className="text-s text-gray-500 mt-1">
          Principal + Interest: ${monthlyPrincipalInterest.toFixed(0)} | Tax: ${monthlyTax.toFixed(0)} | Insurance: ${monthlyInsurance.toFixed(0)} | HOA: ${hoaFees} | MI: ${mortgageInsurance} | Utilities: ${utilityTotal}
        </p>
      </div>
    </div>
  );
};

export default MortgageCalculator;
